import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Lock, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

export default function Checkout() {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchArtist = async () => {
      try {
        const response = await api.getArtist(artistId);
        setArtist(response.data);
      } catch (error) {
        toast.error('Artist not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchArtist();
  }, [artistId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Please write a message for the artist');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.createOrder(artistId, message);
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lime" />
      </div>
    );
  }

  if (!artist) return null;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Order Form */}
            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.5 }}
            >
              <h1 className="font-heading text-3xl font-bold mb-2">Request Dubplate</h1>
              <p className="text-[#A1A1AA] mb-8">
                Write your custom message for {artist.name} below
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="message">Your Message</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={`Hi ${artist.name}! I'd like you to say... [include names, shoutouts, or any specific content you want]`}
                    className="input-dark w-full min-h-[200px] resize-none"
                    data-testid="checkout-message"
                  />
                  <p className="text-xs text-[#666]">
                    Be specific about what you want the artist to say. Include names, dates, or special occasions.
                  </p>
                </div>

                <div className="p-4 bg-surface rounded-xl border border-[#262626]">
                  <div className="flex items-center gap-2 mb-3">
                    <Lock className="w-4 h-4 text-lime" />
                    <span className="text-sm font-medium">Secure Payment</span>
                  </div>
                  <p className="text-xs text-[#A1A1AA]">
                    Your payment will be held securely until the artist delivers your dubplate. 
                    If not delivered within 45 days, you'll receive a full refund.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !message.trim()}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                  data-testid="checkout-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Proceed to Payment - ${artist.price.toFixed(2)}
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-surface border border-[#262626] rounded-2xl overflow-hidden">
                <div className="aspect-video relative">
                  <img
                    src={artist.image_url}
                    alt={artist.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
                </div>
                
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold mb-4">{artist.name}</h3>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#A1A1AA]">Dubplate Price</span>
                      <span className="font-mono">${artist.price.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#A1A1AA]">Delivery Time</span>
                      <span>{artist.delivery_days} days</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#A1A1AA]">Guarantee</span>
                      <span>45 days</span>
                    </div>
                  </div>

                  <div className="border-t border-[#262626] pt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-bold">Total</span>
                      <span className="font-mono text-2xl font-bold text-lime">
                        ${artist.price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* What's Included */}
              <div className="mt-6 p-4 bg-surface-highlight rounded-xl">
                <h4 className="font-bold mb-3">What's Included</h4>
                <ul className="space-y-2 text-sm text-[#A1A1AA]">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-lime rounded-full" />
                    Custom recorded dubplate
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-lime rounded-full" />
                    High-quality audio file
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-lime rounded-full" />
                    Unlimited personal use
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-lime rounded-full" />
                    45-day money-back guarantee
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
